import { createStore } from 'vuex';

import user from '@/store/user.js';
import provider from '@/store/provider.js';
import game from '@/store/game.js';
import globalSearch from '@/store/globalSearch.js';
import filter from '@/store/filter.js';
import assetType from '@/store/assetType.js';
import fileStatus from '@/store/fileStatus.js';
import tag from '@/store/tag.js';
import file from '@/store/file.js';
import chatGPT from '@/store/chatGPT.js';
import multiEdit from '@/store/multiEdit.js';
import popup from '@/store/popup.js';
import role from '@/store/role.js';
import providerAndGame from '@/store/providerAndGame.js';
import smartFilter from '@/store/smartFilter.js';
import color from '@/store/color.js';
import email from '@/store/email.js';
import antifraud from '@/store/antifraud.js';
import billing from '@/store/billing.js';
import corporateRequests from '@/store/corporateRequests.js';
import publisherAdmin from '@/store/publisherAdmin.js';
import partner from '@/store/partner.js';
import userAnalytics from '@/store/userAnalytics.js';
import searchQueryAnalytics from '@/store/searchQueryAnalytics.js';
import systemAnalytics from '@/store/systemAnalytics.js';
import systemSettings from '@/store/systemSettings.js';

import constants from '@/constants';

const store = createStore({
    state: {
        isLoad: false,
        fileInPage: 10,
    },
    getters: {
        FILE_IN_PAGE: (state) => {
            return state.fileInPage;
        },
        IS_LOAD: (state) => {
            return state.isLoad;
        },
    },
    mutations: {
        FILE_IN_PAGE: (state, payload) => {
            state.fileInPage = payload;
        },
        IS_LOAD: (state, payload) => {
            state.isLoad = payload;
        },
    },
    actions: {
        LOGOUT_USER: async function (context) {
            // Clear user authentication data
            await context.dispatch('DELETE_USER');

            // Reset global search state
            await context.dispatch('RESET_PARAMS');

            // Clear file/image data that might be user-specific
            context.state.file.images = [];
            context.state.file.imageCount = 0;
            context.state.file.imageReleased = 0;
            context.state.file.isShowBigPreview = false;
            context.state.file.selectFile = null;

            // Clear role data as it's user session specific
            context.state.role.roles = [];
            context.state.role.isShowRoleEdit = false;
            context.state.role.roleEditData = null;
            context.state.role.isShowRoleAdd = false;
            // Reset roleAddData to default permissions structure
            const defaultRoleData = {
                name: '',
                can_activate_users: false,
                can_add_roles: false,
                can_assign_roles: false,
                can_delete_files: false,
                can_delete_games: false,
                can_edit_asset_types: false,
                can_edit_files: false,
                can_edit_filters: false,
                can_edit_games: false,
                can_edit_prompts: false,
                can_edit_providers: false,
                can_edit_smart_filters: false,
                can_edit_tags: false,
                can_release_files: false,
                can_use_admin: false,
            };
            context.state.role.roleAddData = { ...defaultRoleData };

            // Clear multi-edit data
            context.state.multiEdit.isShowMultiEdit = false;
            context.state.multiEdit.multiEditAssetType = null;
            context.state.multiEdit.multiEditAddFilter = [];
            context.state.multiEdit.multiEditRemoveFilter = [];
            context.state.multiEdit.multiEditReplaceTag = false;
            context.state.multiEdit.multiEditAddTag = [];
            context.state.multiEdit.multiEditRemoveTag = [];
            context.state.multiEdit.multiEditReplaceAdditionalTag = false;
            context.state.multiEdit.multiEditAdditionalTag = '';

            // Clear any popups
            context.state.popup.popups = [];
            context.state.popup.lastID = 0;
            context.state.popup.showPopupName = null;
            context.state.popup.confirmedPopupMethod = null;

            // Reset loading state
            context.commit('IS_LOAD', false);

            // Note: We don't clear theme preferences, providers, games, tags, filters
            // as these should persist and are not user-specific sensitive data
        },
        SAVE_LOCAL_STORAGE: async function (context) {
            const isBlackTheme = context.getters.IS_BLACK_THEME;
            localStorage.setItem('black_theme', isBlackTheme);

            const fileInPage = context.getters.FILE_IN_PAGE;
            localStorage.setItem('file_in_page', fileInPage);

            const mainColor = context.getters.MAIN_COLOR;
            localStorage.setItem('main_color', mainColor);

            const customColor = context.getters.CUSTOM_COLOR.MAIN;
            localStorage.setItem('custom_red', customColor.red);
            localStorage.setItem('custom_green', customColor.green);
            localStorage.setItem('custom_blue', customColor.blue);
        },
        LOAD_LOCAL_STORAGE: async function (context) {
            const isBlackTheme = localStorage.getItem('black_theme') === 'true';
            context.commit('IS_BLACK_THEME', isBlackTheme);

            const bufferFileInPage = localStorage.getItem('file_in_page');
            const fileInPage =
                bufferFileInPage == null ? 10 : Number(bufferFileInPage);
            context.commit('FILE_IN_PAGE', fileInPage);

            const bufferMainColor = localStorage.getItem('main_color');
            const mainColor =
                bufferMainColor == null
                    ? constants.MAIN_COLOR[0].name
                    : bufferMainColor;
            context.commit('MAIN_COLOR', mainColor);

            const bufferCustomRed = localStorage.getItem('custom_red');
            const customRed =
                bufferCustomRed == null ? 255 : Number(bufferCustomRed);
            const bufferCustomGreen = localStorage.getItem('custom_green');
            const customGreen =
                bufferCustomGreen == null ? 255 : Number(bufferCustomGreen);
            const bufferCustomBlue = localStorage.getItem('custom_blue');
            const customBlue =
                bufferCustomBlue == null ? 255 : Number(bufferCustomBlue);

            context.dispatch('CHANGE_ALL_CUSTOM_COLOR', {
                red: customRed,
                green: customGreen,
                blue: customBlue,
                isLoad: true,
            });
            context.dispatch('CHANGE_THEME');
        },
    },
    modules: {
        user,
        role,
        provider,
        game,
        providerAndGame,
        globalSearch,
        multiEdit,
        filter,
        smartFilter,
        assetType,
        fileStatus,
        file,
        tag,
        chatGPT,
        color,
        popup,
        email,
        antifraud,
        billing,
        corporateRequests,
        publisherAdmin,
        partner,
        userAnalytics,
        searchQueryAnalytics,
        systemAnalytics,
        systemSettings,
    },
});

export default store;
